import { useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import { log } from 'src/app/utils/logging';
import { actionUpdateUserEmailAddress } from 'src/app/v1/state/modules/session/userEmailAddress';
import { actionUpdateUserFirstName } from 'src/app/v1/state/modules/session/userFirstName';
import { actionUpdateUserLastName } from 'src/app/v1/state/modules/session/userLastName';
import { actionUpdateHirerInfo } from 'src/app/v1/state/modules/tealium/hirerInfo';
import { useGetCurrentUserLazyQuery } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

export const useCurrentUser = () => {
  const { advertiserId, isDataCleanupComplete } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.advertiserId,
      isDataCleanupComplete: state.app.isDataCleanupComplete,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const [getCurrentUserLazyQuery] = useGetCurrentUserLazyQuery({
    onCompleted: (data) => {
      if (data.currentUser.__typename === 'CurrentUserContext') {
        const { email, firstName, lastName } = data.currentUser;

        dispatch(actionUpdateUserEmailAddress(email));
        dispatch(actionUpdateUserFirstName(firstName));
        dispatch(actionUpdateUserLastName(lastName));

        const tealiumHirerInfo = {
          firstName,
          lastName,
          emailAddress: email,
        };

        dispatch(actionUpdateHirerInfo(tealiumHirerInfo));
      } else if (data.currentUser.__typename === 'MissingUserIdClaimError') {
        log.error('useCurrentUser | getCurrentUserLazyQuery', {
          message: data.currentUser.message,
        });
      } else {
        log.error('useCurrentUser | getCurrentUserLazyQuery', {
          data,
        });
      }
    },
    onError: (error) => {
      log.error('useCurrentUser | getCurrentUserLazyQuery', {
        error,
        advertiserId,
        isDataCleanupComplete,
      });
    },
  });

  useEffect(() => {
    if (isDataCleanupComplete && advertiserId) {
      getCurrentUserLazyQuery();
    }
  }, [advertiserId, dispatch, getCurrentUserLazyQuery, isDataCleanupComplete]);
};
